import {
  GameState,
  InProgressGameState,
  Model,
  User,
} from '@kfiroo/backgammon-core'
import { DocumentReference } from 'firebase/firestore'
import { cloneDeep } from 'lodash'

import {
  createFirestoreGameRepository,
  type GameRepository,
} from '../services/GameRepository'
import {
  BoardInfo,
  PlayersInfo,
  projectBoard,
  projectPlayers,
} from '../services/GameView'

export {
  type AnimatedMove,
  type BoardInfo,
  type LaneInfo,
  type PlayersInfo,
  PlayerType,
} from '../services/GameView'

export abstract class BaseGameApi<StateType extends GameState> {
  protected model: Model
  protected readonly playerIndex: number

  protected constructor(
    protected readonly ref: DocumentReference,
    protected initialModel: Model,
    protected readonly user: User,
    protected readonly repo: GameRepository = createFirestoreGameRepository(
      ref,
    ),
  ) {
    this.model = cloneDeep(initialModel)
    this.playerIndex = this.model.players.findIndex(
      (u) => u?.email === this.user.email,
    )
  }

  get gameId(): string {
    return this.model.gameId
  }

  abstract getBoard(): BoardInfo

  protected buildBoardInfoFromState(state?: InProgressGameState): BoardInfo {
    return projectBoard(state, this.playerIndex)
  }

  /** The model the API currently reflects. Subclasses with their own evolving
   *  state (PlayingGameApi → its PlayController) override this so reads/saves
   *  resolve the live model without a manually-synced shadow field. */
  protected get currentModel(): Model {
    return this.model
  }

  /** The model to PERSIST. Defaults to the currently-reflected model; the active
   *  player overrides this to the committed-board persist model so mid-turn
   *  side-effect writes never persist the tentative board. */
  protected get modelToSave(): Model {
    return this.currentModel
  }

  protected get gameState(): StateType {
    return this.currentModel.gameState as StateType
  }

  getPlayers(): PlayersInfo {
    return projectPlayers(this.currentModel, this.playerIndex)
  }

  protected saveGameModel(model?: Model): Promise<void> {
    return this.repo.save(model ?? this.currentModel)
  }
}
